import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of, map, catchError, tap } from 'rxjs';
import { ConfigService } from './util/config.service';
import { User } from '../types';
import { captureSentryException } from '../utils';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class ExternalApiService {
  private http = inject(HttpClient);
  private configService = inject(ConfigService);
  private authService = inject(AuthService);
  private isSynchronized = signal(false);

  get externalConfig() {
    return this.configService.externalApiConfig;
  }

  synchronizeUserSession(): Observable<User> {
    return this.authService.getUserData();
  }

  synchronizeExternalServices(): Observable<void[]> {
    if (this.isSynchronized()) {
      return of([]);
    }

    if (this.externalConfig.connectedServiceUrls.length === 0) {
      return of([]);
    }

    const requests = this.externalConfig.connectedServiceUrls.map(({ url, route }) => {
      const urlWithRoute = `${url}${route}`;
      return this.http.get<void>(urlWithRoute).pipe(
        catchError(error => {
          console.error(`Error fetching data from ${urlWithRoute}:`, error);
          captureSentryException(error);
          return of(null);
        })
      );
    });

    return forkJoin(requests).pipe(
      tap(() => {
        this.isSynchronized.set(true);
      })
    );
  }

  synchronize(): Observable<void> {
    if (this.isSynchronized()) {
      return of(undefined);
    }

    return forkJoin({
      user: this.synchronizeUserSession(),
      external: this.synchronizeExternalServices(),
    }).pipe(map((): void => undefined)); // just for void type
  }

  resetSynchronization(): void {
    this.isSynchronized.set(false);
  }
}
